import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import './App.css';
import './responsive.css';
import { Auth } from './components/auth';
import { HandleIssue } from './components/handleIssue';
import { UnitSection } from './components/unitSection/unitSection';
import { auth, database } from './firebase';
import { ref, onValue} from "firebase/database";
import { signOut, setPersistence, browserLocalPersistence } from 'firebase/auth';
import { ReactComponent as KamleonLogo } from './img/kamleon_logo.svg';
import { ReactComponent as LogOut } from './img/logout.svg';
import { APP_VERSION, DEFAULT_FILTERS } from './constants/appConstants';
import { handleSortIssue as handleSortIssueUtil } from './components/mainTable/sortingUtils';
import { updateUnitField } from './firebase/firebaseService';
import { refreshIssuesData as refreshIssuesDataUtil, closeAllIssues } from './components/mainTable/issueManagement';
import { fetchUnitsData, fetchIssuesAndHistoricData } from './components/mainTable/dataFetching';
import { updateDashboardUnits as updateDashboardUnitsUtil } from './components/mainTable/dashboardUpdates';
import { validateIssue, getIssueProperty, getIssueCombinedKey } from './schemas/issueSchema';
import { getUnitProperty, getDefaultScheduledWF, getDefaultPostAssemblyCalibration, getDefaultParameters } from './schemas/unitSchema';
import { setDeepValue } from './utils/objectUtils';
import { SensorChartPage } from './components/unitSection/charts/SensorChartPage';
import { DownloadReportButton } from './components/dailyReport/DownloadReportButton';
import MainTable from './components/mainTable/mainTable';
import { onAuthStateChanged } from 'firebase/auth';


function App() {
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [dashboardUnitsComplete, setDashboardUnitsComplete] = useState({});
  const [groupedDataByUnit, setGroupedDataByUnit] = useState({});
  const [data, setData] = useState([]);
  const [unitsFirestoreData, setUnitsFirestoreData] = useState([]);
  const [devicesData, setDevicesData] = useState([]);
  const [unitsData, setUnitsData] = useState([]);
  const [kpodHistoricData, setKpodHistoricData] = useState([]);
  const [dashboardUnitsData, setDashboardUnitsData] = useState([]);
  const [issueGroupToModify, setIssueGroupToModify] = useState({});
  const issueIdFilter = DEFAULT_FILTERS.issueId;

  const [showApp, setShowApp] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(true);
const [issueToModify, setIssueToModify] = useState(null);
  const [sortFieldIssue, setSortFieldIssue] = useState('createdAt');
  const [sortOrderIssue, setSortOrderIssue] = useState('desc');

  const isFetchDataUpdate = useRef(false);

  const navigate = useNavigate();

  const refreshIssuesData = useCallback(async () => {
    const issuesData = await refreshIssuesDataUtil();
    setData(issuesData);
    return issuesData;
  }, []);

  const handleSortIssue = useCallback((field) => {
    handleSortIssueUtil(field, sortFieldIssue, sortOrderIssue, setSortFieldIssue, setSortOrderIssue);
  }, [sortFieldIssue, sortOrderIssue]);

  const syncUnitField = (unitId, field, value) => {
    setDashboardUnitsComplete(prev => ({
      ...prev,
      [unitId]: { ...prev[unitId], [field]: value }
    }));
    setUnitsFirestoreData(prev =>
      prev.map(unit =>
        getUnitProperty(unit, 'unitID') === unitId
          ? { ...unit, [field]: value }
          : unit
      )
    );
  };

  // Generic setter for simple (non-nested) unit fields.
  const setUnitField = async (unitId, fieldName, value) => {
    try {
      await updateUnitField(unitId, fieldName, value);
      syncUnitField(unitId, fieldName, value);
    } catch (error) {
      console.error(`Error setting ${fieldName}:`, error);
    }
  };

  // Generic setter for nested object fields addressed by dot-path
  // (scheduledWF, postAssemblyCalibration, parameters): merges fieldPath/value
  // into the current object (or a default shape) and writes the whole object.
  const setUnitDeepField = async (unitId, fieldName, fieldPath, value, getDefault) => {
    try {
      const current = dashboardUnitsComplete[unitId]?.[fieldName] || getDefault();
      const updated = setDeepValue(current, fieldPath, value);
      await updateUnitField(unitId, fieldName, updated);
      syncUnitField(unitId, fieldName, updated);
    } catch (error) {
      console.error(`Error setting ${fieldName}:`, error);
    }
  };

  const setNewIsSmartActionsEnabled = (unitId, enabled) => setUnitField(unitId, 'isSmartActionsEnabled', enabled);
  const setNewScheduledWF = (unitId, fieldPath, value) =>
    setUnitDeepField(unitId, 'scheduledWF', fieldPath, value, getDefaultScheduledWF);
  const setNewPostAssemblyCalibration = (unitId, fieldPath, value) =>
    setUnitDeepField(unitId, 'postAssemblyCalibration', fieldPath, value, getDefaultPostAssemblyCalibration);
  const setNewParameters = (unitId, fieldPath, value) =>
    setUnitDeepField(unitId, 'parameters', fieldPath, value, getDefaultParameters);

  const classifiedIssuesbyUnitAndKey = useMemo(() => {
    return data.reduce((result, item) => {
      // Use schema validation
      if (!validateIssue(item)) {
        console.warn('Invalid issue found in classifiedIssuesbyUnitAndKey:', item);
        return result;
      }
      
      const unitKey = getIssueProperty(item, 'Installation.Unit_ID');
      const combinedKey = getIssueCombinedKey(item);
      
      if (!unitKey || !combinedKey) {
        console.warn('Missing unitKey or combinedKey for issue:', item);
        return result;
      }

      if (!result[unitKey]) {
        result[unitKey] = {};
      }
      if (!result[unitKey][combinedKey]) {
        result[unitKey][combinedKey] = [];
      }
      result[unitKey][combinedKey].push(item);
      return result;
    }, {});
  }, [data]);

  const updateDashboardUnits = useCallback(async (
    updateType = 'all',
    dataInput = undefined,
    devicesDataInput = undefined,
    dashboardUnitsDataInput = undefined,
    unitsDataInput = undefined,
    unitsFirestoreDataInput = undefined,
  ) => {
    const result = await updateDashboardUnitsUtil(
      updateType,
      dataInput,
      devicesDataInput,
      dashboardUnitsDataInput,
      unitsDataInput,
      unitsFirestoreDataInput,
      data,
      devicesData,
      dashboardUnitsData,
      unitsData,
      unitsFirestoreData,
      dashboardUnitsComplete
    );

    setGroupedDataByUnit(result.groupedDataByUnit);

    return result.dashboardUnits;
  }, [data, devicesData, dashboardUnitsData, unitsData, unitsFirestoreData, dashboardUnitsComplete]);

  const fetchData = useCallback(async () => {
    try {
      // Phase 1: fetch units — show the page as soon as this resolves
      const {
        unitsData: fetchedUnitsData,
        devicesData: fetchedDevicesData,
        unitsFirestoreData: fetchedUnitsFirestoreData,
        dashboardUnitsData: fetchedDashboardUnitsData
      } = await fetchUnitsData();

      // Build dashboard directly with fetched data (bypasses stale useCallback closure)
      const phase1Result = await updateDashboardUnitsUtil(
        'all',
        [],                       // no issues yet
        fetchedDevicesData,
        fetchedDashboardUnitsData,
        fetchedUnitsData,
        fetchedUnitsFirestoreData,
        [], [], [], [], [], {}     // empty current-state fallbacks
      );

      // Set all state and mark flag so the useEffect watcher skips this update
      isFetchDataUpdate.current = true;
      setUnitsData(fetchedUnitsData);
      setDevicesData(fetchedDevicesData);
      setUnitsFirestoreData(fetchedUnitsFirestoreData);
      setDashboardUnitsData(fetchedDashboardUnitsData);
      setGroupedDataByUnit(phase1Result.groupedDataByUnit);
      setDashboardUnitsComplete(phase1Result.dashboardUnits);

      // Phase 2: fetch issues in the background
      fetchIssuesAndHistoricData().then(async ({ kamEyeData, kpodHistoricData }) => {
        const phase2Result = await updateDashboardUnitsUtil(
          'all',
          kamEyeData,
          fetchedDevicesData,
          fetchedDashboardUnitsData,
          fetchedUnitsData,
          fetchedUnitsFirestoreData,
          [], [], [], [], [], {}
        );

        isFetchDataUpdate.current = true;
        setData(kamEyeData);
        setKpodHistoricData(kpodHistoricData);
        setGroupedDataByUnit(phase2Result.groupedDataByUnit);
        setDashboardUnitsComplete(phase2Result.dashboardUnits);
      }).catch(error => {
        console.error('Error fetching issues data:', error);
      });

      return { kamEyeData: [], dashboardUnits: phase1Result.dashboardUnits };
    } catch (error) {
      console.error('Error fetching data:', error);
      throw error;
    }
  }, []);

  useEffect(() => {
    if (!showApp) return;

    // Skip if this is from the initial fetch to prevent double update
    if (isFetchDataUpdate.current) {
      isFetchDataUpdate.current = false;
      return;
    }

    const updateDashboard = async () => {
      const dashboardData = await updateDashboardUnits();
      setDashboardUnitsComplete(dashboardData);
    };

    updateDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, unitsData, showApp]);

  useEffect(() => {
    if (!showApp) return;

    const unitsRef = ref(database, "/units");
    const kamEyeRef = ref(database, "/kam_eye");

  const unsubscribers = [];

  // Units listener
  unsubscribers.push(
    onValue(unitsRef, (snapshot) => {
      const data = snapshot.val();
      const unitsArray = data 
        ? Object.entries(data).map(([id, value]) => ({ ...value, id })) 
        : [];
      setUnitsData(unitsArray);
    })
  );

  // Issues listener  
  unsubscribers.push(
    onValue(kamEyeRef, (snapshot) => {
      const data = snapshot.val();
      const issuesArray = data
        ? Object.entries(data).map(([id, value]) => ({ ...value, id }))
        : [];
      setData(issuesArray);
    })
  );

  // Cleanup
  return () => {
    unsubscribers.forEach(unsubscribe => unsubscribe());
  };
}, [showApp]);

    useEffect(() => {
    const initializeAuth = async () => {
      try {
        await setPersistence(auth, browserLocalPersistence);

        const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
          if (!user) {
            setShowAuthModal(true);
            setShowApp(false);
            setIsAuthLoading(false); // Done loading
            console.log('No user is currently authenticated.');
            return;
          }

          if (!user.email.endsWith('@kamleon.com')) {
            console.warn('Unauthorized domain, signing out...');
            await signOut(auth);
            sessionStorage.clear();
            setShowAuthModal(true);
            setShowApp(false);
            setIsAuthLoading(false);
            return;
          }

          await fetchData();
          setShowApp(true);
          setShowAuthModal(false);
          setIsAuthLoading(false); // Done loading

          navigate('/', { replace: true });
        });

        return unsubscribeAuth;
      } catch (error) {
        console.error('Error initializing auth:', error);
        setIsAuthLoading(false); // Done loading even on error
      }
    };

    initializeAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      sessionStorage.clear();
      setShowAuthModal(true);
      setShowApp(false);
      navigate('/');
    } catch (error) {
      console.log(error);
    }
  };

  const handleUnit = (unit) => {
    const unitId = getUnitProperty(unit, 'unit');
    if (!unitId) {
      console.warn('Unit missing ID, cannot navigate:', unit);
      return;
    }
    navigate(`/issues?unit=${unitId}`);
  }

  const backToDashboard = () => {
    navigate('/');
  }

  const handleHandleIssue = (item, issueGroup) => {
    // Validate the main issue
    if (!validateIssue(item)) {
      console.warn('Invalid issue passed to handleHandleIssue:', item);
      return;
    }
    
    setIssueToModify(item);
    
    if (issueGroup && issueGroup.length > 1) {
      // Validate all issues in the group
      const validIssues = issueGroup.filter(issue => {
        if (!validateIssue(issue)) {
          console.warn('Invalid issue in group:', issue);
          return false;
        }
        return true;
      });
      setIssueGroupToModify(validIssues);
    } else {
      setIssueGroupToModify([]);
    }
    
    // Use schema-safe property access for navigation
    const unitId = getIssueProperty(item, 'Installation.Unit_ID');
    
    if (!unitId) {
      console.warn('Issue missing Unit_ID, cannot navigate:', item);
      return;
    }
    
    // Navigate to the handle issue route
    navigate(`/handle-issue?id=${item.id}`);
  };
const handleCloseAllIssues = async (issuesToClose) => {
  try {
    await closeAllIssues(issuesToClose);
    
    // Refresh the issues data
    await refreshIssuesData();
    
  } catch (error) {
    console.error('Error in handleCloseAllIssues:', error);
  }
};
  
  return (
    <div className="App">
      {isAuthLoading && (
        <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}
      {!isAuthLoading && showApp && (
        <div >
          <div className="row pt-1 pb-2 mx-0" style={{ borderBottom: '1px solid grey', overflowX: 'hidden' }}>
            <div className="col-6 pe-0 ps-3">
              <div className="text-start">
                <KamleonLogo style={{ width: '115px', height: '16px' }} />
              </div>
            </div>
            <div className="col-6 ps-0 pe-3">
              <div className="d-flex align-items-center">
                <div className="ms-auto d-flex align-items-center">
                  <DownloadReportButton />
                  <button
                    type="button"
                    className="btn btn-link px-0"
                    onClick={handleLogout}
                  >
                    <LogOut style={{ width: '24px', height: '24px' }} />
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="container">
            <Routes>
              <Route path="/" element={
                <MainTable
                  dashboardUnitsComplete={dashboardUnitsComplete}
                  groupedDataByUnit={groupedDataByUnit}
                  classifiedIssuesbyUnitAndKey={classifiedIssuesbyUnitAndKey}
                  handleUnit={handleUnit}
                  handleCloseAllIssues={handleCloseAllIssues}
                />
              }/>
              <Route path="/issues" element={
                <UnitSection
                  classifiedIssuesbyUnitAndKey={classifiedIssuesbyUnitAndKey}
                  issueIdFilter={issueIdFilter}
                  handleSortIssue={handleSortIssue}
                  sortFieldIssue={sortFieldIssue}
                  sortOrderIssue={sortOrderIssue}
                  handleHandleIssue={handleHandleIssue}
                  dashboardUnitsComplete={dashboardUnitsComplete}
                  setNewIsSmartActionsEnabled={setNewIsSmartActionsEnabled}
                  setNewScheduledWF={setNewScheduledWF}
                  setNewPostAssemblyCalibration={setNewPostAssemblyCalibration}
                  setNewParameters={setNewParameters}
                  backToDashboard={backToDashboard}
                  kpodHistoricData={kpodHistoricData}
                  handleCloseAllIssues={handleCloseAllIssues}
                />
              } />
              <Route path="/handle-issue" element={
                <HandleIssue
                  issue={issueToModify}
                  issueGroup={issueGroupToModify}
                  onClose={async (issueWasModified = false) => {
                    if (issueWasModified) {
                      await refreshIssuesData(); // This now also refreshes dashboard units
                    }
                    
                    // Use schema-safe property access for navigation
                    const unitId = getIssueProperty(issueToModify, 'Installation.Unit_ID', '');
                    
                    navigate(`/issues?unit=${unitId}`);
                  }}
                  onIssueUpdated={refreshIssuesData} // This also refreshes dashboard units
                />
              } />
              <Route path="/sensor-chart" element={
                  <SensorChartPage  />
                } />
            </Routes>
            <div className="text-center mt-3 text-muted"><i>Version {APP_VERSION}</i></div>
          </div>
        </div>
      )}
      {!isAuthLoading && showAuthModal && (
        <Auth
          onClose={() => {
            setShowAuthModal(false);
            setShowApp(true);
          }}
        />
      )}
    </div>
  );
}


export default App;